import { useState } from 'react'
import { useCreateResource, useUpdateResource } from '../resources/hooks/useResourceMutation'
import getCollection from '../resources/support/getCollection'
import { logger } from '../utils/logging'
import { Entity } from '../resources/types'

interface CreateAutoBuildResult {
  appId: string | null
  isLoading: boolean
  error: Error | null
}

/**
 * Hook for creating an autobuild app with proper naming
 */
export function useCreateAutoBuildApp() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [createApp] = useCreateResource('APPS')
  const [updateAccount] = useUpdateResource('ACCOUNTS')

  /**
   * Creates a new autobuild app and optionally sets it as the default app
   */
  const createAutoBuildApp = async (options?: { setAsDefaultApp?: boolean }): Promise<CreateAutoBuildResult> => {
    const { setAsDefaultApp = false } = options || {}
    setIsLoading(true)
    setError(null)
    const prefix = 'AutoBuild App'
    const namespacePrefix = 'autobuild_app'

    try {
      // Get the apps collection and refresh to ensure we have the latest data
      const appsCollection = getCollection('APPS')
      await appsCollection.refresh()

      // Filter for autobuild apps by namespace to avoid conflicts
      const autoBuildApps = appsCollection
        .getItems()
        .filter((app) => app.namespace?.startsWith(namespacePrefix) || app.name?.startsWith(prefix))

      // Generate a new name with an incremented number
      let newAppName = prefix
      let newNamespace = namespacePrefix

      if (autoBuildApps.length > 0) {
        const highestNumber = autoBuildApps.reduce((max: number, a: Entity<'APPS'>) => {
          // Check both namespace and name for backwards compatibility
          let numberFromNamespace = 0
          let numberFromName = 0

          // Extract number from namespace (e.g., "autobuild_app_3" -> 3)
          if (a.namespace?.startsWith(namespacePrefix)) {
            const namespaceMatch = a.namespace.match(new RegExp(`${namespacePrefix}_(\\d+)$`))
            if (namespaceMatch) {
              numberFromNamespace = parseInt(namespaceMatch[1], 10)
            }
          }

          // Extract number from name for backwards compatibility (e.g., "AutoBuild App 2" -> 2)
          if (a.name?.startsWith(prefix)) {
            const nameMatch = a.name.match(new RegExp(`${prefix} (\\d+)$`))
            if (nameMatch) {
              numberFromName = parseInt(nameMatch[1], 10)
            }
          }

          return Math.max(max, numberFromNamespace, numberFromName)
        }, 0)

        const nextNumber = highestNumber + 1
        newAppName = `${prefix} ${nextNumber}`
        newNamespace = `${namespacePrefix}_${nextNumber}`
      }

      // Create the new app
      const newApp = await createApp({
        name: newAppName,
        namespace: newNamespace,
        type: 'CUSTOM',
        metadata: { autobuild: true },
      })

      // Update collections to include the new app
      appsCollection.append(newApp)

      // Set as default app if requested
      if (setAsDefaultApp) {
        try {
          await updateAccount({ defaultAppId: newApp.id })
        } catch (updateError) {
          logger.error('Failed to set default app', { error: updateError })
          // Continue even if setting default app fails
        }
      }

      return { appId: newApp.id, isLoading: false, error: null }
    } catch (err) {
      const creationError = err instanceof Error ? err : new Error('Failed to create autobuild app')
      logger.error('Failed to create autobuild app', { error: err })
      setError(creationError)
      return { appId: null, isLoading: false, error: creationError }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    createAutoBuildApp,
    isLoading,
    error,
  }
}
